import { Typography } from '@mui/material';
import { isEmpty, omit } from 'es-toolkit/compat';
import React from 'react';

import { downloadSecondaryAnalysisPolygonsForStudy } from 'api/customerResults';
import { DownloadFilesModal } from 'components/atoms/DownloadFilesModal';
import { getPolygonDownloadParams, PolygonsExportScope } from 'services/export/polygonsExportScope';
import { manualSelectionFilters } from 'utils/useCasesParams/constants';
import { ExportScopeField, ExportScopeFields } from './ExportFormFields';
import { ExportFormShell } from './ExportFormShell';
import { excludedCasesOptions, getCasesFieldLabel, getScopeOptionLabels } from './exportScopeLabels';
import { ResultsExportContext } from './useResultsExportContext';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { StartExport } from 'services/export/useSeparateOutputExports';

interface PolygonsExportFormProps {
  context: ResultsExportContext;
  startExport: StartExport;
  isExportRunning: boolean;
  onBack: () => void;
}

export const PolygonsExportForm: React.FC<PolygonsExportFormProps> = ({
  context,
  startExport,
  isExportRunning,
  onBack,
}) => {
  const { study, studyId, labId, isSlidesMode } = context;
  const labels = getScopeOptionLabels(isSlidesMode);

  const scopeOptions: { value: PolygonsExportScope; label: string }[] = [
    ...(context.hasSpecificSelections ? [{ value: 'selected' as const, label: labels.selected }] : []),
    ...(context.areFiltersAppliedExceptStudy ? [{ value: 'filtered' as const, label: labels.filtered }] : []),
    { value: 'whole', label: labels.whole },
  ];

  const { query, update } = useResultsExportUrlState();
  // A stale URL value (for example a selection that no longer exists) falls back to the default scope.
  const scope =
    scopeOptions.find((option) => option.value === query.polygonsExportScope)?.value ?? scopeOptions[0].value;
  const excludedCases = query.polygonsExportExcludedCases === 'with' ? 'with' : 'without';
  const [bundleUrl, setBundleUrl] = React.useState<string | null>(null);

  const bundleFileName = `secondary_analysis_polygons_${studyId}.zip`;
  const filesToDownload = React.useMemo(
    () => (bundleUrl ? [{ title: bundleFileName, url: bundleUrl, fileName: bundleFileName }] : undefined),
    [bundleUrl, bundleFileName]
  );

  const downloadParams = getPolygonDownloadParams({
    scope,
    excludedCases,
    labId,
    studyId,
    casesParams: context.casesParams,
  });

  const filteredScopeHasNoFilters =
    scope === 'filtered' && isEmpty(omit(context.casesParams, manualSelectionFilters).filters);

  const startPolygonExport = (): void => {
    void startExport({
      title: 'Secondary analysis polygons export',
      canCancel: true,
      run: async (signal) => {
        const blob = await downloadSecondaryAnalysisPolygonsForStudy({ ...downloadParams, signal });
        setBundleUrl(URL.createObjectURL(blob));
      },
    });
  };

  return (
    <>
      <ExportFormShell
        study={study}
        scope={
          <ExportScopeFields>
            <ExportScopeField
              id="polygons-cases"
              label={getCasesFieldLabel(isSlidesMode)}
              options={scopeOptions}
              value={scope}
              onChange={(value) => update({ polygonsExportScope: value })}
            />
            {scope === 'whole' && (
              <ExportScopeField
                id="polygons-excluded-cases"
                label="Excluded cases"
                options={excludedCasesOptions}
                value={excludedCases}
                onChange={(value) => update({ polygonsExportExcludedCases: value === 'with' ? 'with' : undefined })}
              />
            )}
          </ExportScopeFields>
        }
        options={
          <Typography variant="body2" color="text.secondary">
            One ZIP archive of the secondary analysis polygons, if available. It is not a cell-table file or a prepared
            table bundle.
          </Typography>
        }
        primaryLabel="Download"
        primaryDisabled={isExportRunning || filteredScopeHasNoFilters}
        primaryGuidance={
          filteredScopeHasNoFilters ? 'No filters are applied to export secondary analysis polygons.' : ''
        }
        onPrimary={startPolygonExport}
        onBack={onBack}
      />
      {Boolean(filesToDownload) && (
        <DownloadFilesModal
          open
          onClose={() => setBundleUrl(null)}
          filesToDownload={filesToDownload}
          dialogTitle="Download requested secondary analysis polygons"
        />
      )}
    </>
  );
};
